import { useMemo } from "react";
import { differenceInDays } from "date-fns";
import Button from "../../../ui/Button/Button";
import styles from "./UsageSummary.module.scss";
import { BillingTier } from "../../../__generated__/gql/graphql";
import { SolidScript } from "../../../ui/Icon/Icon";
import ProgressBar from "../../../ui/ProgressBar/ProgressBar";
import ChangePlanModal from "../../../pages/Settings/Billing/PlanCard/ChangePlanModal/ChangePlanModal";
import { useDisclosure } from "../../../ui/util/useDisclosure";
import useUser from "../../../providers/UserProvider/useUser";
import SecondTeamWarningModal from "../../SecondTeamWarningModal/SecondTeamWarningModal";

const BillingTierMap: Record<BillingTier, string> = {
  [BillingTier.Free]: "Free",
  [BillingTier.Starter]: "Starter",
  [BillingTier.Pro]: "Pro",
  [BillingTier.Scale]: "Scale",
};

function getUsageResetTimeframe(daysUntilReset: number) {
  const weeks = Math.floor(daysUntilReset / 7);

  if (weeks > 0) {
    return `${weeks} week${weeks > 1 ? "s" : ""}`;
  }

  return `${daysUntilReset} day${daysUntilReset > 1 ? "s" : ""}`;
}

function UsageSummary() {
  const { billingInfo, usageForCurrentPeriod } = useUser();

  const [changePlanModalOpened, changePlanModal] = useDisclosure();
  const [secondTeamWarningModalOpened, secondTeamWarningModal] =
    useDisclosure();

  const daysUntilReset = useMemo(
    () =>
      billingInfo.currentMonthEnd
        ? differenceInDays(new Date(billingInfo.currentMonthEnd), new Date())
        : 0,
    [billingInfo],
  );

  return (
    <div className={styles.container}>
      <div className={styles.plan}>
        <SolidScript className={styles.icon} size="large" />
        <span className={styles.planName}>
          {`${BillingTierMap[billingInfo.billingTier]} Plan`}
        </span>
      </div>
      {!!billingInfo.articlesPerMonth && (
        <>
          <div className={styles.articleCount}>
            <span>Articles generated</span>
            <span>
              {`${usageForCurrentPeriod.articles} / ${billingInfo.articlesPerMonth}`}
            </span>
          </div>
          <ProgressBar
            maxValue={billingInfo.articlesPerMonth}
            currentValue={usageForCurrentPeriod.articles}
          />
          <div className={styles.usageResets}>
            <span>
              Usage resets in {getUsageResetTimeframe(daysUntilReset)}
            </span>
          </div>
        </>
      )}
      {billingInfo.billingTier === BillingTier.Free &&
      billingInfo.freeCreditsIssued &&
      billingInfo.creditsUsed + billingInfo.credits <=
        billingInfo.freeCreditsIssued ? (
        <>
          <div className={styles.articleCount}>
            <span>Articles</span>
            <span>{`${billingInfo.freeCreditsIssued - billingInfo.credits} / ${
              billingInfo.freeCreditsIssued
            }`}</span>
          </div>
          <ProgressBar
            maxValue={billingInfo.freeCreditsIssued}
            currentValue={billingInfo.freeCreditsIssued - billingInfo.credits}
          />
        </>
      ) : (
        billingInfo.credits > 0 && (
          <div className={styles.articleCount}>
            <span>Other Credits</span>
            <span>{`${billingInfo.credits} remaining`}</span>
          </div>
        )
      )}
      {billingInfo.billingTier === BillingTier.Free && (
        <>
          {billingInfo.credits === 0 && billingInfo.creditsUsed === 0 ? (
            <>
              <div className={styles.articleCount}>
                <span>You have no credits</span>
              </div>
              <Button
                onClick={secondTeamWarningModal.open}
                size="small"
                variant="outline"
              >
                View options
              </Button>
            </>
          ) : (
            <Button
              onClick={changePlanModal.open}
              size="small"
              variant="outline"
            >
              Upgrade plan
            </Button>
          )}
        </>
      )}
      <ChangePlanModal
        isOpen={changePlanModalOpened}
        onClose={changePlanModal.close}
      />
      <SecondTeamWarningModal
        isOpen={secondTeamWarningModalOpened}
        onClose={secondTeamWarningModal.close}
      />
    </div>
  );
}

export default UsageSummary;
